import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Badge,
  Button,
  Drawer,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BgColorsOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  getModules,
  getAdminFormGroups,
  getDetailConfig,
  saveDetailConfig,
  getModuleDetailPreview,
  getAvailableCollections,
  getCollectionFields,
  getDetailDefaults,
  saveDetailDefaults,
} from '../../../services/adminApi';
import {
  getDefaults,
  applyDefaults,
  loadDetailDefaults,
  BUILT_IN_DEFAULTS,
} from '../../../services/detailDefaults';
import AppCard from '../../../components/cards/AppCard';
import OtpGateModal from '../../../components/admin/OtpGateModal';
import FieldValue from '../../../components/detail/fieldRenderEngine';
import { RENDER_TYPES, DOCUMENT_MODES, TEXT_TRANSFORMS } from '../../../components/detail/renderConfig';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;

// OTP-gated config actions for the detail-view config, mirroring Form Groups.
const CONFIG_GATE_META = {
  reset:   { action: 'reset',   title: 'Reset all detail config',  confirmLabel: 'Reset All',       danger: true,
    description: 'Clears every detail-view override for all modules in this workspace, so the detail view falls back to the plain form-group fields.' },
  save:    { action: 'save',    title: 'Save current as default',  confirmLabel: 'Save Default',    danger: false,
    description: 'Snapshots the current detail config for all modules as the saved default you can restore to later.' },
  restore: { action: 'restore', title: 'Restore saved default',    confirmLabel: 'Restore Default', danger: true,
    description: 'Replaces the current detail config for all modules with the last saved default.' },
};


// capitalize is a tri-state (*bool on the backend): unset → default behaviour,
// true → force title-case, false → leave the raw value alone.
const CAP_OPTIONS = [
  { label: 'Default',        value: 'default' },
  { label: 'Title Case On',  value: 'on' },
  { label: 'Off (raw text)', value: 'off' },
];
const capToValue = (c) => (c === true ? 'on' : c === false ? 'off' : 'default');
const valueToCap = (v) => (v === 'on' ? true : v === 'off' ? false : undefined);

// Flatten the module's form-groups into a de-duplicated field universe that the
// detail config overrides sit on top of.
function flattenFields(groups) {
  const seen = new Set();
  const out = [];
  for (const g of groups ?? []) {
    for (const f of g.fields ?? []) {
      if (!f.field || seen.has(f.field)) continue;
      seen.add(f.field);
      out.push({
        key: f.field,
        label: f.label ?? f.field,
        type: f.type ?? 'text',
        group: g.label ?? g.name,
      });
    }
  }
  return out;
}

// Strip an override down to only the keys that actually change something, so the
// saved config stays minimal (and a field with no overrides is dropped).
function cleanFieldCfg(field, c) {
  if (!c) return null;
  const out = { field };
  if (c.label) out.label = c.label;
  if (c.prefix) out.prefix = c.prefix;
  if (c.suffix) out.suffix = c.suffix;
  if (c.color) out.color = c.color;
  if (c.capitalize === true || c.capitalize === false) out.capitalize = c.capitalize;
  if (c.hidden === true || c.hidden === false) out.hidden = c.hidden;
  // Render type is the single source of truth — migrate the legacy
  // documentPreview / displayAs flags into it on save.
  const rt = c.renderType
    || (c.documentPreview ? 'document' : c.displayAs === 'tag' ? 'tag' : c.displayAs === 'text' ? 'text' : '');
  if (rt && rt !== 'auto') out.renderType = rt;
  const ro = {};
  for (const [k, v] of Object.entries(c.renderOptions ?? {})) {
    if (v !== undefined && v !== '' && v !== false) ro[k] = v;
  }
  if (Object.keys(ro).length) out.renderOptions = ro;
  if (c.lookupCollection) {
    out.lookupCollection = c.lookupCollection;
    out.displayField = c.displayField || '';
    if (c.displayField2) out.displayField2 = c.displayField2;
    out.valueField = c.valueField || '_id';
  }
  return Object.keys(out).length > 1 ? out : null;
}

function cleanMerges(merges) {
  return (merges ?? [])
    .filter((m) => m.key && (m.fields ?? []).length)
    .map((m) => {
      const out = { key: m.key, label: m.label || m.key, fields: m.fields };
      if (m.template) out.template = m.template;
      if (m.valueMaps && Object.keys(m.valueMaps).length) out.valueMaps = m.valueMaps;
      if (m.separator) out.separator = m.separator;
      if (m.prefix) out.prefix = m.prefix;
      if (m.suffix) out.suffix = m.suffix;
      if (m.color) out.color = m.color;
      if (m.capitalize === true || m.capitalize === false) out.capitalize = m.capitalize;
      if (m.group) out.group = m.group;
      if (m.hideSources === false) out.hideSources = false;
      return out;
    });
}

function fieldShowToHidden(field) {
  return Number(field?.show ?? 1) === 0;
}

function syncGroupsWithDetailVisibility(groups, fieldCfg) {
  const changed = [];
  const nextGroups = (groups ?? []).map((group) => {
    let groupChanged = false;
    const fields = (group.fields ?? []).map((field) => {
      const cfg = fieldCfg[field.field];
      if (!cfg || cfg.hidden === undefined) return field;

      const nextShow = cfg.hidden ? 0 : 1;
      if (Number(field.show ?? 1) === nextShow) return field;

      groupChanged = true;
      return { ...field, show: nextShow };
    });

    const nextGroup = groupChanged ? { ...group, fields } : group;
    if (groupChanged) changed.push(nextGroup);
    return nextGroup;
  });

  return { nextGroups, changed };
}

// ── Colour input (text + native swatch) ─────────────────────────────────────────
function ColorInput({ value, onChange }) {
  return (
    <Space.Compact style={{ width: '100%' }}>
      <Input
        size="small"
        placeholder="#0053a5"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="fg-mono-input"
        prefix={<BgColorsOutlined style={{ color: value || '#bbb' }} />}
      />
      <input
        type="color"
        value={/^#[0-9a-f]{6}$/i.test(value ?? '') ? value : '#0053a5'}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 32, border: '1px solid #d9d9d9', borderLeft: 0, cursor: 'pointer', padding: 0 }}
        aria-label="Pick colour"
      />
    </Space.Compact>
  );
}

// ── Field Override Drawer ────────────────────────────────────────────────────────
function FieldOverrideDrawer({ open, fieldMeta, value, onClose, onSave }) {
  // The parent keys this component per field, so it remounts on every open —
  // a useState initializer seeds the draft (no prop→state effect needed).
  const [draft, setDraft] = useState(() => ({ ...(value ?? {}) }));
  const [collections, setCollections] = useState([]);
  const [collFields, setCollFields] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getAvailableCollections()
      .then((c) => { if (!cancelled) setCollections(c.map((x) => (typeof x === 'string' ? x : x.name ?? x.value ?? '')).filter(Boolean)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || !draft.lookupCollection) return undefined;
    let cancelled = false;
    getCollectionFields(draft.lookupCollection)
      .then((f) => { if (!cancelled) setCollFields(f.map((x) => (typeof x === 'string' ? x : x.value ?? x.field ?? x.label ?? '')).filter(Boolean)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, draft.lookupCollection]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  // Clearing collFields in the handler (not an effect) keeps suggestions fresh.
  const setCollection = (v) => { setCollFields([]); setDraft((d) => ({ ...d, lookupCollection: v })); };
  const collOptions = collections.map((c) => ({ value: c }));
  const fieldOptions = collFields.map((c) => ({ value: c }));

  // Render type drives the detail view. Derive from legacy flags for old configs.
  const renderOpts = draft.renderOptions ?? {};
  const setOpt = (k, v) => set('renderOptions', { ...renderOpts, [k]: v || undefined });
  const effectiveRenderType = draft.renderType
    || (draft.documentPreview ? 'document' : draft.displayAs === 'tag' ? 'tag' : draft.displayAs === 'text' ? 'text' : 'auto');
  const isTagLike = ['tag', 'tags', 'chip', 'badge'].includes(effectiveRenderType);
  const isTransformable = ['text', 'tag', 'tags', 'chip', 'badge', 'link', 'email', 'phone'].includes(effectiveRenderType);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={760}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal"
      title={
        <span className="fg-drawer-title">
          <EditOutlined /> {fieldMeta?.label}
          <code className="fg-field-key-hint" style={{ marginLeft: 8 }}>{fieldMeta?.key}</code>
        </span>
      }
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={() => onSave(draft)}>Apply</Button>
        </div>
      }
    >
      <div className="fg-form-section">
        <div className="fg-form-section-label">Display</div>

        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Label override</Text>
          <Input size="small" placeholder={fieldMeta?.label} value={draft.label ?? ''} onChange={(e) => set('label', e.target.value)} />
        </div>

        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Prefix</Text>
            <Input size="small" placeholder="e.g. $" value={draft.prefix ?? ''} onChange={(e) => set('prefix', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Suffix</Text>
            <Input size="small" placeholder="e.g. yrs" value={draft.suffix ?? ''} onChange={(e) => set('suffix', e.target.value)} />
          </div>
        </div>

        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Colour</Text>
            <ColorInput value={draft.color} onChange={(v) => set('color', v)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Capitalize</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              options={CAP_OPTIONS}
              value={capToValue(draft.capitalize)}
              onChange={(v) => set('capitalize', valueToCap(v))}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={!!draft.hidden} onChange={(v) => set('hidden', v)} />
          <Text style={{ fontSize: 13 }}>Hide this field in the detail view</Text>
        </div>

        <div style={{ marginTop: 14 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Render Type</Text>
          <Select
            size="small"
            style={{ width: '100%' }}
            value={effectiveRenderType}
            onChange={(v) => set('renderType', v)}
            options={RENDER_TYPES}
          />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            How this field is displayed in the detail view — entirely config-driven.
          </Text>
        </div>

        {/* Render-type-specific options */}
        {effectiveRenderType === 'document' && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Document mode</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={renderOpts.documentMode || 'preview'}
              onChange={(v) => setOpt('documentMode', v)}
              options={DOCUMENT_MODES}
            />
          </div>
        )}
        {isTransformable && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Text transform</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={renderOpts.textTransform || 'none'}
              onChange={(v) => setOpt('textTransform', v === 'none' ? undefined : v)}
              options={TEXT_TRANSFORMS}
            />
          </div>
        )}
        {isTagLike && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Tag colour</Text>
            <Input size="small" placeholder="geekblue / #0053a5" value={renderOpts.tagColor ?? ''} onChange={(e) => setOpt('tagColor', e.target.value)} className="fg-mono-input" />
          </div>
        )}
        {effectiveRenderType === 'date' && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Date format (dayjs tokens)</Text>
            <Input size="small" placeholder="DD/MM/YYYY" value={renderOpts.dateFormat ?? ''} onChange={(e) => setOpt('dateFormat', e.target.value)} className="fg-mono-input" />
          </div>
        )}
        {(effectiveRenderType === 'currency' || effectiveRenderType === 'budget') && (
          <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Currency symbol</Text>
              <Input size="small" placeholder="$ / ₹ / £" value={renderOpts.currencySymbol ?? ''} onChange={(e) => setOpt('currencySymbol', e.target.value)} />
            </div>
            {effectiveRenderType === 'budget' && (
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Unit (e.g. Monthly)</Text>
                <Input size="small" placeholder="Monthly" value={renderOpts.unit ?? ''} onChange={(e) => setOpt('unit', e.target.value)} />
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={!!renderOpts.showEmpty} onChange={(v) => setOpt('showEmpty', v)} />
          <Text style={{ fontSize: 13 }}>Show empty values</Text>
        </div>
      </div>

      <div className="fg-form-section">
        <div className="fg-form-section-label">Reference Lookup (id → name)</div>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          For fields that store a foreign-key id. The detail API resolves the name from the
          target collection and returns <code>{'{ id, value }'}</code>.
        </Text>

        <div style={{ marginBottom: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Collection</Text>
          <AutoComplete
            size="small"
            style={{ width: '100%' }}
            options={collOptions}
            placeholder="e.g. vendors"
            value={draft.lookupCollection ?? ''}
            onChange={setCollection}
            filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
            className="fg-mono-input"
          />
        </div>

        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Display Field</Text>
            <AutoComplete
              size="small"
              style={{ width: '100%' }}
              options={fieldOptions}
              placeholder="e.g. vendorCompanyName"
              value={draft.displayField ?? ''}
              onChange={(v) => set('displayField', v)}
              filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
              className="fg-mono-input"
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Display Field 2</Text>
            <AutoComplete
              size="small"
              style={{ width: '100%' }}
              options={fieldOptions}
              placeholder="e.g. lastName"
              value={draft.displayField2 ?? ''}
              onChange={(v) => set('displayField2', v)}
              filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
              className="fg-mono-input"
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Value Field (FK)</Text>
            <Input size="small" placeholder="_id" value={draft.valueField ?? ''} onChange={(e) => set('valueField', e.target.value)} className="fg-mono-input" />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Merge Drawer ──────────────────────────────────────────────────────────────
// flattenValueMaps turns the saved { field: { raw: display } } shape into flat
// editor rows; buildValueMaps does the reverse.
function flattenValueMaps(valueMaps) {
  const rows = [];
  Object.entries(valueMaps ?? {}).forEach(([field, mapping]) => {
    Object.entries(mapping ?? {}).forEach(([from, to]) => rows.push({ field, from, to }));
  });
  return rows;
}

function buildValueMaps(rows) {
  const out = {};
  for (const r of rows ?? []) {
    if (!r.field || !r.from) continue;
    out[r.field] = { ...(out[r.field] ?? {}), [r.from]: r.to ?? '' };
  }
  return out;
}

function MergeDrawer({ open, merge, fieldOptions, groupOptions, onClose, onSave }) {
  // Parent keys this per merge → remounts on open, so the initializer seeds it.
  const [draft, setDraft] = useState(() => ({
    key: '', label: '', fields: [], separator: ' - ', template: '',
    prefix: '', suffix: '', color: '', capitalize: undefined,
    group: '', hideSources: true,
    ...(merge ?? {}),
    mapRows: flattenValueMaps(merge?.valueMaps),
  }));

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updateRow = (i, patch) => setDraft((d) => {
    const rows = [...(d.mapRows ?? [])];
    rows[i] = { ...rows[i], ...patch };
    return { ...d, mapRows: rows };
  });
  const removeRow = (i) => setDraft((d) => ({ ...d, mapRows: (d.mapRows ?? []).filter((_, idx) => idx !== i) }));
  const addRow = () => setDraft((d) => ({
    ...d, mapRows: [...(d.mapRows ?? []), { field: d.fields?.[0] ?? '', from: '', to: '' }],
  }));

  const handleSave = () => {
    if (!draft.key?.trim()) return message.error('A merge key is required');
    if (!/^[a-z0-9_]+$/.test(draft.key)) return message.error('Key: lowercase letters, numbers, underscores only');
    if (!(draft.fields ?? []).length) return message.error('Pick at least one source field');
    const { mapRows, ...rest } = draft;
    onSave({ ...rest, valueMaps: buildValueMaps(mapRows) });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={820}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal"
      title={<span className="fg-drawer-title"><MergeCellsOutlined /> {merge ? 'Edit Merged Field' : 'New Merged Field'}</span>}
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Apply</Button>
        </div>
      }
    >
      <div className="fg-form-section">
        <div className="fg-form-section-label">Merged Field</div>

        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Key</Text>
            <Input size="small" placeholder="experience_range" value={draft.key ?? ''} onChange={(e) => set('key', e.target.value)} className="fg-mono-input" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Label</Text>
            <Input size="small" placeholder="Experience" value={draft.label ?? ''} onChange={(e) => set('label', e.target.value)} />
          </div>
        </div>

        <div style={{ margin: '12px 0' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Source fields (combined in order)</Text>
          <Select
            mode="multiple"
            size="small"
            style={{ width: '100%' }}
            placeholder="Pick fields to combine"
            options={fieldOptions}
            value={draft.fields ?? []}
            onChange={(v) => set('fields', v)}
            optionFilterProp="label"
          />
        </div>

        {/* Template — full control over layout & literals. Overrides Separator. */}
        <div style={{ margin: '12px 0' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Template <span style={{ color: '#94a3b8' }}>(optional — overrides separator)</span>
          </Text>
          <Input.TextArea
            rows={2}
            value={draft.template ?? ''}
            onChange={(e) => set('template', e.target.value)}
            placeholder="{clientBudgetStart} - {clientBudgetEnd} {clientBudgetCurrency}/{clientBudgetUnit}"
            className="fg-mono-input"
          />
          {(draft.fields ?? []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
              {(draft.fields ?? []).map((f) => (
                <Tag
                  key={f}
                  color="blue"
                  style={{ cursor: 'pointer', margin: 0 }}
                  onClick={() => set('template', `${draft.template ?? ''}{${f}}`)}
                >
                  + {`{${f}}`}
                </Tag>
              ))}
            </div>
          )}
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
            Click a chip to insert a field. Everything else (spaces, “/”, “-”) is kept literally.
            Result e.g. <code>60 - 80 $/Monthly</code>.
          </Text>
        </div>

        {/* Per-field value maps — e.g. Monthly → mon. Unmapped values pass through. */}
        <div style={{ margin: '12px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Value mappings (raw → display)</Text>
            <Button size="small" type="dashed" icon={<PlusOutlined />} disabled={!(draft.fields ?? []).length} onClick={addRow}>
              Add
            </Button>
          </div>
          {(draft.mapRows ?? []).map((row, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
              <Select
                size="small"
                style={{ flex: 1.4 }}
                placeholder="field"
                value={row.field || undefined}
                options={(draft.fields ?? []).map((f) => ({ value: f, label: f }))}
                onChange={(v) => updateRow(i, { field: v })}
              />
              <Input size="small" style={{ flex: 1 }} placeholder="Monthly" value={row.from} onChange={(e) => updateRow(i, { from: e.target.value })} />
              <span style={{ color: '#94a3b8' }}>→</span>
              <Input size="small" style={{ flex: 1 }} placeholder="mon" value={row.to} onChange={(e) => updateRow(i, { to: e.target.value })} />
              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeRow(i)} />
            </div>
          ))}
          {!(draft.mapRows ?? []).length && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              e.g. Monthly → mon, Daily → day, Yearly → yr. Unmapped values stay as-is.
            </Text>
          )}
        </div>

        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Separator <span style={{ color: '#94a3b8' }}>(used when no template)</span></Text>
            <Input size="small" placeholder=" - " value={draft.separator ?? ''} onChange={(e) => set('separator', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Place in group</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              allowClear
              placeholder="(first source's group)"
              options={groupOptions}
              value={draft.group || undefined}
              onChange={(v) => set('group', v ?? '')}
            />
          </div>
        </div>

        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Prefix</Text>
            <Input size="small" value={draft.prefix ?? ''} onChange={(e) => set('prefix', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Suffix</Text>
            <Input size="small" placeholder="yrs" value={draft.suffix ?? ''} onChange={(e) => set('suffix', e.target.value)} />
          </div>
        </div>

        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Colour</Text>
            <ColorInput value={draft.color} onChange={(v) => set('color', v)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Capitalize</Text>
            <Select size="small" style={{ width: '100%' }} options={CAP_OPTIONS} value={capToValue(draft.capitalize)} onChange={(v) => set('capitalize', valueToCap(v))} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={draft.hideSources !== false} onChange={(v) => set('hideSources', v)} />
          <Text style={{ fontSize: 13 }}>Hide the source fields after merging</Text>
        </div>
      </div>
    </Modal>
  );
}

// ── Preview Drawer ──────────────────────────────────────────────────────────────
function PreviewDrawer({ open, module, onClose }) {
  const [recordId, setRecordId] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    if (!recordId.trim()) return message.warning('Enter a record _id to preview');
    setLoading(true); setError('');
    try {
      // Load saved display defaults alongside the record so the preview renders
      // with the configured S3 base / date format / labels.
      const [data] = await Promise.all([
        getModuleDetailPreview(module, recordId.trim()),
        loadDetailDefaults(),
      ]);
      setGroups(data);
      if (!data.length) setError('No groups returned — check the id and module.');
    } catch (e) {
      setError(e?.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={<span className="fg-drawer-title"><EyeOutlined /> Detail Preview — {module}</span>}
      classNames={{ header: 'fg-drawer-header', body: 'fg-drawer-body' }}
    >
      <Space.Compact style={{ width: '100%', marginBottom: 14 }}>
        <Input
          placeholder="Paste a record _id (24-char ObjectId)"
          value={recordId}
          onChange={(e) => setRecordId(e.target.value)}
          onPressEnter={load}
          className="fg-mono-input"
        />
        <Button type="primary" loading={loading} onClick={load}>Load</Button>
      </Space.Compact>

      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        {groups.map((g) => (
          <div key={g.id ?? g.name} style={{ marginBottom: 18 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>{g.label ?? g.name}</Text>
            {(g.fields ?? []).filter((f) => f.show === undefined || f.show === 1).map((f) => (
              <div key={f.field} style={{ display: 'flex', gap: 10, padding: '4px 0', borderBottom: '1px dashed #eee' }}>
                <Text type="secondary" style={{ fontSize: 12, width: 150, flexShrink: 0 }}>{f.label}</Text>
                <FieldValue field={f} />
              </div>
            ))}
          </div>
        ))}
        {!loading && !groups.length && !error && (
          <Empty description="Load a record to preview its detail view." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Spin>
    </Drawer>
  );
}

// ── Display Defaults Drawer ─────────────────────────────────────────────────────
// Tenant-wide settings that used to be hard-coded in fileUtils.js / FieldValue.jsx.
function pickDefaults(d) {
  const out = {};
  ['s3BaseUrl', 'dateFormat', 'booleanTrueLabel', 'booleanFalseLabel', 'emptyPlaceholder', 'separator'].forEach((k) => {
    if (d?.[k] !== undefined && d?.[k] !== '') out[k] = d[k];
  });
  if (Array.isArray(d?.documentNameOrder) && d.documentNameOrder.length) out.documentNameOrder = d.documentNameOrder;
  return out;
}

function DefaultsDrawer({ open, onClose }) {
  const [draft, setDraft] = useState(() => ({ ...getDefaults() }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getDetailDefaults()
      .then((d) => { if (!cancelled) setDraft((prev) => ({ ...prev, ...pickDefaults(d) })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        s3BaseUrl: (draft.s3BaseUrl ?? '').trim(),
        dateFormat: (draft.dateFormat ?? '').trim(),
        booleanTrueLabel: draft.booleanTrueLabel ?? '',
        booleanFalseLabel: draft.booleanFalseLabel ?? '',
        emptyPlaceholder: draft.emptyPlaceholder ?? '',
        separator: draft.separator ?? '',
        documentNameOrder: Array.isArray(draft.documentNameOrder) ? draft.documentNameOrder : [],
      };
      await saveDetailDefaults(payload);
      applyDefaults(payload); // live-update the singleton so detail views reflect immediately
      message.success('Display defaults saved');
      onClose();
    } catch (e) {
      message.error(e?.message || 'Could not save defaults');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: 12, display: 'block', marginBottom: 4 };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={460}
      title={<span className="fg-drawer-title"><SettingOutlined /> Display Defaults</span>}
      classNames={{ header: 'fg-drawer-header', body: 'fg-drawer-body' }}
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={() => setDraft({ ...BUILT_IN_DEFAULTS })}>Reset to built-in</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save Defaults</Button>
        </div>
      }
    >
      <div className="fg-form-section">
        <div className="fg-form-section-label">Documents / Files</div>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={labelStyle}>S3 Base URL</Text>
          <Input size="small" placeholder={BUILT_IN_DEFAULTS.s3BaseUrl} value={draft.s3BaseUrl ?? ''} onChange={(e) => set('s3BaseUrl', e.target.value)} className="fg-mono-input" />
        </div>
        <div>
          <Text type="secondary" style={labelStyle}>Document name priority (first match wins)</Text>
          <Select
            mode="tags"
            size="small"
            style={{ width: '100%' }}
            placeholder="name, documentName, uploadedFileName…"
            value={draft.documentNameOrder ?? []}
            onChange={(v) => set('documentNameOrder', v)}
          />
        </div>
      </div>

      <div className="fg-form-section">
        <div className="fg-form-section-label">Value Formatting</div>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={labelStyle}>Date format (dayjs tokens)</Text>
          <Input size="small" placeholder="DD MMM YYYY" value={draft.dateFormat ?? ''} onChange={(e) => set('dateFormat', e.target.value)} className="fg-mono-input" />
        </div>
        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={labelStyle}>Boolean — true</Text>
            <Input size="small" placeholder="Yes" value={draft.booleanTrueLabel ?? ''} onChange={(e) => set('booleanTrueLabel', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={labelStyle}>Boolean — false</Text>
            <Input size="small" placeholder="No" value={draft.booleanFalseLabel ?? ''} onChange={(e) => set('booleanFalseLabel', e.target.value)} />
          </div>
        </div>
        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={labelStyle}>Empty placeholder</Text>
            <Input size="small" placeholder="-" value={draft.emptyPlaceholder ?? ''} onChange={(e) => set('emptyPlaceholder', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={labelStyle}>Object / array separator</Text>
            <Input size="small" placeholder=" - " value={draft.separator ?? ''} onChange={(e) => set('separator', e.target.value)} />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

// ── Main Section ──────────────────────────────────────────────────────────────
export default function DetailGroupsSection() {
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [selected, setSelected] = useState('');

  const [groups, setGroups] = useState([]); // raw form-groups (listed like FormGroups)
  const [fields, setFields] = useState([]); // flattened — used for merge field options
  const [groupNames, setGroupNames] = useState([]);
  const [fieldCfg, setFieldCfg] = useState({}); // { [fieldKey]: override }
  const [merges, setMerges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [editField, setEditField] = useState(null); // field meta being edited
  const [mergeDraft, setMergeDraft] = useState(null); // { index, merge } | { index:null }
  const [previewOpen, setPreviewOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [gate, setGate] = useState(null); // OTP-gated config action meta
  const [refreshKey, setRefreshKey] = useState(0);

  function openGate(action) { setGate(CONFIG_GATE_META[action]); }

  // After the OTP modal verifies + performs the action, re-fetch the selected
  // module's config (bump refreshKey, which the loader effect depends on).
  function handleGateVerified() {
    setGate(null);
    setRefreshKey((k) => k + 1);
  }

  useEffect(() => {
    getModules()
      .then((mods) => {
        setModules(mods);
        if (mods.length) setSelected(mods[0].key);
      })
      .catch(() => setModules([]))
      .finally(() => setModulesLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const [groupData, cfg] = await Promise.all([getAdminFormGroups(selected), getDetailConfig(selected)]);
        if (cancelled) return;
        setGroups(groupData ?? []);
        setFields(flattenFields(groupData));
        setGroupNames((groupData ?? []).map((g) => ({ value: g.name, label: g.label ?? g.name })));
        const map = {};
        (cfg.fields ?? []).forEach((fc) => { map[fc.field] = fc; });
        setFieldCfg(map);
        setMerges(cfg.merges ?? []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load configuration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected, refreshKey]);

  const fieldOptions = useMemo(() => fields.map((f) => ({ value: f.key, label: `${f.label} (${f.key})` })), [fields]);

  async function handleSave() {
    setSaving(true);
    try {
      const fieldsPayload = Object.entries(fieldCfg).map(([k, c]) => cleanFieldCfg(k, c)).filter(Boolean);
      const { nextGroups, changed } = syncGroupsWithDetailVisibility(groups, fieldCfg);
      await saveDetailConfig({ module: selected, fields: fieldsPayload, merges: cleanMerges(merges) });
      if (changed.length) {
        setGroups(nextGroups);
        setFields(flattenFields(nextGroups));
        setGroupNames(nextGroups.map((g) => ({ value: g.name, label: g.label ?? g.name })));
      }
      message.success('Detail view configuration saved');
    } catch (e) {
      message.error(e?.message || 'Could not save configuration');
    } finally {
      setSaving(false);
    }
  }

  function applyFieldOverride(draft) {
    setFieldCfg((prev) => ({ ...prev, [editField.key]: { ...draft, field: editField.key } }));
    setEditField(null);
  }

  function clearFieldOverride(key) {
    setFieldCfg((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function applyMerge(draft) {
    setMerges((prev) => {
      const next = [...prev];
      if (mergeDraft.index === null) next.push(draft);
      else next[mergeDraft.index] = draft;
      return next;
    });
    setMergeDraft(null);
  }

  // Quick inline hidden toggle
  function toggleHidden(key, hidden) {
    setFieldCfg((prev) => {
      const cur = prev[key] ?? { field: key };
      return { ...prev, [key]: { ...cur, hidden } };
    });
  }

  const overrideTags = (cfg) => {
    if (!cfg) return null;
    const tags = [];
    if (cfg.label) tags.push(<Tag key="l" color="blue">label</Tag>);
    if (cfg.prefix || cfg.suffix) tags.push(<Tag key="ps" color="geekblue">±fix</Tag>);
    if (cfg.color) tags.push(<Tag key="c" color={cfg.color}>colour</Tag>);
    if (cfg.capitalize === true) tags.push(<Tag key="cap" color="purple">Title</Tag>);
    if (cfg.capitalize === false) tags.push(<Tag key="cap" color="default">raw</Tag>);
    if (cfg.lookupCollection) tags.push(<Tag key="lk" color="volcano">lookup: {cfg.lookupCollection}</Tag>);
    const rt = cfg.renderType || (cfg.documentPreview ? 'document' : cfg.displayAs);
    if (rt && rt !== 'auto') tags.push(<Tag key="rt" color="cyan">{rt}</Tag>);
    return tags.length ? tags : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
  };

  // Groups listed exactly like FormGroupsSection (same getAdminFormGroups API),
  // each row expandable to its fields — but the per-field action configures the
  // DETAIL override instead of editing the group.
  const groupColumns = [
    {
      title: 'Group',
      dataIndex: 'label',
      key: 'label',
      render: (label, g) => <Text strong style={{ color: 'var(--color-text-heading)' }}>{label ?? g.name}</Text>,
    },
    { title: 'Key', dataIndex: 'name', key: 'name', render: (name) => <code className="fg-table-code">{name}</code> },
    { title: 'Order', dataIndex: 'order', key: 'order', width: 72, align: 'center', render: (v) => <span className="fg-order-cell">{v ?? 0}</span> },
    {
      title: 'Fields', dataIndex: 'fields', key: 'fields', width: 76, align: 'center',
      render: (f) => <Badge count={f?.length ?? 0} showZero color="var(--color-brand)" />,
    },
    {
      title: 'Customized', key: 'cust', width: 100, align: 'center',
      render: (_, g) => {
        const n = (g.fields ?? []).filter((f) => fieldCfg[f.field]).length;
        return n ? <Tag color="blue" style={{ margin: 0 }}>{n}</Tag> : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
      },
    },
  ];

  // Expanded rows: the fields inside a group, each with its detail-override
  // controls (visibility toggle, customize, reset).
  const renderGroupFields = (group) => {
    const fieldsArr = group.fields ?? [];
    if (!fieldsArr.length) {
      return <Text type="secondary" style={{ fontSize: 12 }}>No fields in this group.</Text>;
    }
    return (
      <div className="fg-expanded-fields">
        {fieldsArr.map((f) => {
          const cfg = fieldCfg[f.field];
          const hidden = cfg?.hidden ?? fieldShowToHidden(f);
          const meta = { key: f.field, label: f.label ?? f.field, type: f.type ?? 'text', group: group.label ?? group.name };
          return (
            <div key={f.field} className="fg-field-row">
              <div className="fg-field-row-info">
                <span className="fg-field-row-label">{cfg?.label || f.label}</span>
                <span className="fg-field-row-key">{f.field}</span>
                <span className="fg-field-row-type">({f.type})</span>
              </div>
              <div className="fg-field-row-flags">{overrideTags(cfg)}</div>
              <Space size={4} style={{ marginLeft: 'auto' }}>
                <Tooltip title={hidden ? 'Hidden in form and detail view' : 'Visible'}>
                  <Switch size="small" checkedChildren="Hide" unCheckedChildren="Show" checked={hidden} onChange={(v) => toggleHidden(f.field, v)} />
                </Tooltip>
                <Tooltip title="Customize field"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditField(meta)} /></Tooltip>
                {cfg && <Tooltip title="Reset to default"><Button type="text" size="small" danger icon={<ReloadOutlined />} onClick={() => clearFieldOverride(f.field)} /></Tooltip>}
              </Space>
            </div>
          );
        })}
      </div>
    );
  };

  const sortedGroups = [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const mergeColumns = [
    { title: 'Label', dataIndex: 'label', key: 'label', render: (l, r) => <span><Text strong>{l || r.key}</Text><span className="fg-field-key-hint">{r.key}</span></span> },
    { title: 'Fields', dataIndex: 'fields', key: 'fields', render: (f) => <Space size={4} wrap>{(f ?? []).map((x) => <Tag key={x}>{x}</Tag>)}</Space> },
    {
      title: 'Format', key: 'sep', width: 220,
      render: (_, r) => <code className="fg-table-code">{r.template?.trim() ? r.template : `join: ${r.separator || ' - '}`}</code>,
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      align: 'right',
      render: (_, r, i) => (
        <Space size={4}>
          <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => setMergeDraft({ index: i, merge: r })} /></Tooltip>
          <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => setMerges((prev) => prev.filter((_, idx) => idx !== i))} /></Tooltip>
        </Space>
      ),
    },
  ];

  const moduleTabs = modules.map((m) => ({ key: m.key, label: m.label }));

  return (
    <div className="fg-section">
      <AppCard
        icon={<BgColorsOutlined />}
        title="Detail Groups"
        extra={
          <Space size={8}>
            <Tooltip title="Clear all detail-view overrides for every module (OTP verified)">
              <Button size="small" danger icon={<ReloadOutlined />} onClick={() => openGate('reset')}>Reset All</Button>
            </Tooltip>
            <Tooltip title="Save the current detail config for all modules as the default (OTP verified)">
              <Button size="small" icon={<SaveOutlined />} onClick={() => openGate('save')}>Save Default</Button>
            </Tooltip>
            <Tooltip title="Restore all modules from the last saved default (OTP verified)">
              <Button size="small" icon={<CloudDownloadOutlined />} onClick={() => openGate('restore')}>Restore Default</Button>
            </Tooltip>
            <Button size="small" icon={<SettingOutlined />} onClick={() => setDefaultsOpen(true)}>Display Defaults</Button>
            <Button size="small" icon={<EyeOutlined />} disabled={!selected} onClick={() => setPreviewOpen(true)}>Preview</Button>
            <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} disabled={!selected} onClick={handleSave}>
              Save Configuration
            </Button>
          </Space>
        }
      >
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          Customize how each module&apos;s detail view renders: per-field label, prefix/suffix, colour,
          capitalization, hide, reference lookups (id → name), and merged display fields. Applied on top
          of Form Groups — the add/edit forms are unaffected.
        </Text>
      </AppCard>

      <AppCard>
        <Spin spinning={modulesLoading}>
          <Tabs activeKey={selected} onChange={setSelected} items={moduleTabs.map((t) => ({ ...t, children: null }))} />
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

          <Spin spinning={loading}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>Field Groups</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{groups.length} groups · {fields.length} fields</Text>
            </div>
            <Table
              rowKey={(g) => g.id ?? g.name}
              dataSource={sortedGroups}
              columns={groupColumns}
              pagination={false}
              size="small"
              className="fg-table"
              expandable={{ expandedRowRender: renderGroupFields, rowExpandable: () => true }}
              locale={{ emptyText: <Empty description="Select a module to see its groups and fields." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />

            <div style={{ margin: '20px 0 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong><MergeCellsOutlined /> Merged Fields</Text>
              <Button size="small" type="dashed" icon={<PlusOutlined />} disabled={!selected} onClick={() => setMergeDraft({ index: null, merge: null })}>
                Add Merge
              </Button>
            </div>
            <Table
              rowKey="key"
              dataSource={merges}
              columns={mergeColumns}
              pagination={false}
              size="small"
              className="fg-table"
              locale={{ emptyText: <Empty description="No merged fields. Combine e.g. 'from' + 'to' into one." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </Spin>
        </Spin>
      </AppCard>

      <FieldOverrideDrawer
        key={editField?.key ?? 'field-closed'}
        open={!!editField}
        fieldMeta={editField}
        value={editField ? fieldCfg[editField.key] : null}
        onClose={() => setEditField(null)}
        onSave={applyFieldOverride}
      />

      <MergeDrawer
        key={mergeDraft ? `merge-${mergeDraft.index ?? 'new'}` : 'merge-closed'}
        open={!!mergeDraft}
        merge={mergeDraft?.merge}
        fieldOptions={fieldOptions}
        groupOptions={groupNames}
        onClose={() => setMergeDraft(null)}
        onSave={applyMerge}
      />

      <DefaultsDrawer open={defaultsOpen} onClose={() => setDefaultsOpen(false)} />

      <PreviewDrawer open={previewOpen} module={selected} onClose={() => setPreviewOpen(false)} />

      <OtpGateModal
        open={!!gate}
        scope="detailConfig"
        action={gate?.action}
        title={gate?.title}
        description={gate?.description}
        confirmLabel={gate?.confirmLabel}
        danger={gate?.danger}
        onCancel={() => setGate(null)}
        onVerified={handleGateVerified}
      />
    </div>
  );
}
